/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from 'src/state'
import { RECENTS_KEY_NAME } from '../utils'
import HomeSidebarList from './HomeSidebarList'
import UpgradeBanner from 'src/components/UpgradeBanner'
import { SidebarHeader, SidebarInner, SidebarWrap } from './SidebarComponents'
import { Alert, Spin } from 'antd'
import Text from 'src/components/ts-components/Text'

const AccountType = {
  FREE: 1,
  PROFESSIONAL: 2,
  ULTIMATE: 3,
}

const HOME_SIDEBAR_TUTORIAL_KEY = 'SIDEBAR_STATE'

const tabOrder = [
  'organizations',
  'competitions',
  'teams',
  'sandboxLeagues',
  'recentOrganizations',
  'recentCompetitions',
  'recentTeams',
]

const filterForAdminAndUpdatePermission = (response) => {
  const { data, meta } = response
  if (!data || !meta || !meta?.permissions) {
    return []
  }

  const onlyNonSandbox = data.filter(
    (org) => !org.name_full.includes('Sandbox')
  )

  return onlyNonSandbox.filter((d) => {
    const permission = meta.permissions[d?.id]
    return permission?.update || permission?.admin
  })
}

const filterForUpdatePermission = (response) => {
  const { data, meta } = response
  if (!data || !meta || !meta?.permissions) {
    return []
  }
  const onlyNonSandbox = data.filter(
    (org) => !org.name_full.includes('Sandbox')
  )
  return onlyNonSandbox.filter((d) => {
    const permission = meta.permissions[d?.id]
    return permission?.update || permission?.post
  })
}

const encodeTabStates = (states) => {
  return tabOrder.map((tab) => (states[tab] ? '1' : '0')).join('')
}

const decodeTabStates = (encodedStates) => {
  const states = {}
  encodedStates.split('').forEach((state, index) => {
    states[tabOrder[index]] = state === '1'
  })
  return states
}

const HomeSidebar = observer(
  ({
    user,
    me,
    refreshFeed,
    isLoggedIn,
    setFeedOrgs,
    setFeedTeams,
    setFeedScheds,
  }) => {
    const [tabStates, setTabStates] = useState({
      organizations: true,
      competitions: false,
      teams: false,
      sandboxLeagues: true,
      recentOrganizations: false,
      recentCompetitions: false,
      recentTeams: false,
    })

    const toggleTabState = async (tabName) => {
      const newTabStates = {
        ...tabStates,
        [tabName]: !tabStates[tabName],
      }
      setTabStates(newTabStates)

      try {
        const encodedStates = encodeTabStates(newTabStates)
        localStorage.setItem(HOME_SIDEBAR_TUTORIAL_KEY, encodedStates)
      } catch (e) {
        console.error('Failed to save tab states:', e)
      }
    }

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const [orgs, setOrgs] = useState([])
    const [scheds, setSched] = useState([])
    const [teams, setTeams] = useState([])

    const fetchTabStates = useCallback(async () => {
      try {
        const tabs = localStorage.getItem(HOME_SIDEBAR_TUTORIAL_KEY)
        if (tabs) {
          const savedTabStates = decodeTabStates(tabs)
          setTabStates((prevState) => ({ ...prevState, ...savedTabStates }))
        } else {
          // If we have no saved tab states, we need to set the default ones
          if (teams.length > 0) {
            setTabStates((prevState) => ({ ...prevState, teams: true }))
          } else if (scheds.length > 0) {
            setTabStates((prevState) => ({ ...prevState, competitions: true }))
          } else if (orgs.length > 0) {
            setTabStates((prevState) => ({ ...prevState, organizations: true }))
          }
        }
      } catch (e) {
        console.error('Failed to fetch tab states:', e)
      }
    }, [teams.length, scheds.length, orgs.length])

    useEffect(() => {
      setLoading(true)
      setError(false)
      const promises = [
        req('/users/me/organizations?per_page=50&sort=name_full&direction=asc'),
        req('/users/me/schedules?per_page=50&sort=starts_at&direction=desc'),
        req('/users/me/teams?per_page=50&sort=name_full&direction=asc'),
        fetchTabStates(),
      ]
      Promise.all(promises)
        .then(([org, sched, teams]) => {
          setOrgs(filterForAdminAndUpdatePermission(org))
          setSched(sched?.data || [])
          setTeams(teams?.data || [])
          setFeedOrgs(filterForUpdatePermission(org))
          setFeedTeams(filterForUpdatePermission(teams))
          setFeedScheds(filterForUpdatePermission(sched))
          setLoading(false)
          setError(false)
        })
        .catch((e) => {
          setError(e?.message || 'There was a problem.')
          setLoading(false)
        })
    }, [])

    const [recents, setRecents] = useState({})
    const {
      me: {
        state: { account },
      },
    } = useMobxStore()

    const isBusinessAccount = useMemo(() => {
      return me?.account_id && me?.account_type && me?.account_type > 1
    }, [me])

    const shouldDisplayUpgradeBanner = useMemo(() => {
      return (
        Number(account?.subscription_type?.id) === AccountType.FREE &&
        isBusinessAccount &&
        !isCanlan
      )
    }, [account?.subscription_type?.id, isBusinessAccount])

    useEffect(() => {
      try {
        const jsonFromStorage = localStorage.getItem(RECENTS_KEY_NAME)
        if (jsonFromStorage) {
          const object = JSON.parse(jsonFromStorage)
          if (object?.uid === user.id) {
            setRecents(object)
          } else {
            localStorage.removeItem(RECENTS_KEY_NAME)
          }
        }
      } catch (e) {}
    }, [user.id])

    return (
      <SidebarWrap>
        <SidebarInner>
          <SidebarHeader
            imageId={user?.imageId}
            name={user?.fullName}
            subTitle='View Profile'
            to={ROUTES.PROFILE.ROOT}
          />
          {shouldDisplayUpgradeBanner ? (
            <div
              css={css`
                margin-top: 16px;
                margin-bottom: 8px;
              `}
            >
              <UpgradeBanner />
            </div>
          ) : null}
          <div
            css={css`
              margin-top: 16px;
              margin-bottom: 8px;
              height: 1px;
              background: ${colors.SOFT_STEEL};
            `}
          />
          {error ? (
            <Alert
              message={error}
              type='error'
              showIcon
              css={css`
                margin-top: 8px;
                margin-bottom: 8px;
                font-size: 14px;
              `}
            />
          ) : null}
          {loading ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 16px 0;
              `}
            >
              <Spin size='small' />
            </div>
          ) : (
            <>
              {orgs.length > 0 ? (
                <>
                  <HomeSidebarList
                    title={`${
                      isCanlan ? 'Leagues/Organizations' : 'Organizations'
                    }`}
                    data={orgs}
                    icon={
                      <ApartmentOutlined
                        css={css`
                          font-size: 16px;
                          color: ${colors.PRIMARY}!important;
                        `}
                      />
                    }
                    baseRoute={ROUTES.ORG_ROOT}
                    fallback='/images/generic_placeholders/placeholder_organization.png'
                    isOpen={tabStates.organizations}
                    onToggle={() => toggleTabState('organizations')}
                    displayGetStarted={false}
                  />
                </>
              ) : null}
              {scheds.length > 0 ? (
                <>
                  <HomeSidebarList
                    title={`${isCanlan ? 'Seasons' : 'Competitions'}`}
                    data={scheds}
                    icon={
                      <TrophyOutlined
                        css={css`
                          font-size: 16px;
                          color: ${colors.PRIMARY}!important;
                        `}
                      />
                    }
                    baseRoute={ROUTES.SCHEDULE_ROOT}
                    fallback='/images/generic_placeholders/placeholder_competition.png'
                    isOpen={tabStates.competitions}
                    onToggle={() => toggleTabState('competitions')}
                  />
                </>
              ) : null}
              {teams.length > 0 ? (
                <>
                  <HomeSidebarList
                    title='Teams'
                    data={teams}
                    icon={
                      <SafetyCertificateOutlined
                        css={css`
                          font-size: 16px;
                          color: ${colors.PRIMARY}!important;
                        `}
                      />
                    }
                    baseRoute={ROUTES.TEAM_ROOT}
                    fallback='/images/generic_placeholders/placeholder_team.png'
                    isOpen={tabStates.teams}
                    onToggle={() => toggleTabState('teams')}
                  />
                </>
              ) : null}
              {!isCanlan && me?.account_id && !loading ? (
                <>
                  <HomeSidebarList
                    title='Demo Leagues'
                    data={[
                      {
                        id: 'soccer-sandbox-leagues',
                        fullName: 'Soccer League',
                        onClick: () => {},
                      },
                      {
                        id: 'hockey-sandbox-leagues',
                        fullName: 'Hockey League',
                      },
                    ]}
                    icon={
                      <TrophyOutlined
                        css={css`
                          font-size: 16px;
                          color: ${colors.PRIMARY}!important;
                        `}
                      />
                    }
                    baseRoute={ROUTES.SANDBOX}
                    fallback='/images/generic_placeholders/placeholder_competition.png'
                    displayCount={false}
                    isOpen={tabStates.sandboxLeagues}
                    onToggle={() => toggleTabState('sandboxLeagues')}
                  />
                </>
              ) : null}
              {recents[ENTITY_TYPES.org] &&
                recents[ENTITY_TYPES.org].filter(
                  (item) =>
                    item.id !== account?.organization?.url.split('/').pop()
                ).length > 0 && (
                  <>
                    <HomeSidebarList
                      title={`Recent ${isCanlan ? 'Leagues' : 'Organizations'}`}
                      data={recents[ENTITY_TYPES.org].filter(
                        (item) =>
                          item.id !==
                          account?.organization?.url.split('/').pop()
                      )}
                      icon={
                        <ClockCircleOutlined
                          css={css`
                            font-size: 16px;
                            color: ${colors.PRIMARY}!important;
                          `}
                        />
                      }
                      baseRoute={ROUTES.ORG_ROOT}
                      fallback='/images/generic_placeholders/placeholder_organization.png'
                      displayCount={false}
                      isOpen={tabStates.recentOrganizations}
                      onToggle={() => toggleTabState('recentOrganizations')}
                    />
                  </>
                )}

              {recents[ENTITY_TYPES.schedule] &&
                recents[ENTITY_TYPES.schedule]?.length > 0 && (
                  <>
                    <HomeSidebarList
                      title={`Recent ${isCanlan ? 'Seasons' : 'Competitions'}`}
                      data={recents[ENTITY_TYPES.schedule]}
                      icon={
                        <ClockCircleOutlined
                          css={css`
                            font-size: 16px;
                            color: ${colors.PRIMARY}!important;
                          `}
                        />
                      }
                      baseRoute={ROUTES.SCHEDULE_ROOT}
                      fallback='/images/generic_placeholders/placeholder_competition.png'
                      displayCount={false}
                      isOpen={tabStates.recentCompetitions}
                      onToggle={() => toggleTabState('recentCompetitions')}
                    />
                  </>
                )}
              {recents[ENTITY_TYPES.team] &&
                recents[ENTITY_TYPES.team]?.length > 0 && (
                  <>
                    <HomeSidebarList
                      title='Recent Teams'
                      data={recents[ENTITY_TYPES.team]}
                      icon={
                        <ClockCircleOutlined
                          css={css`
                            font-size: 16px;
                            color: ${colors.PRIMARY}!important;
                          `}
                        />
                      }
                      baseRoute={ROUTES.TEAM_ROOT}
                      fallback='/images/generic_placeholders/placeholder_team.png'
                      displayCount={false}
                      isOpen={tabStates.recentTeams}
                      onToggle={() => toggleTabState('recentTeams')}
                    />
                  </>
                )}
            </>
          )}
        </SidebarInner>
      </SidebarWrap>
    )
  }
)

export default HomeSidebar
